import React, { useEffect, useMemo } from 'react';

import { V1CloudEventsResourceType } from '@ariksa/audittrail/api';
import { Box, HStack, SimpleGrid, Stack } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { customTheme } from 'theme';

import {
  DetailsIdentifier,
  getIconColor,
  renderRiskContext,
  StackedHeader,
  Tag,
} from 'components/DataDisplay';
import { Drawer } from 'components/Overlay';
import { CDashboard } from 'components/Visualization';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { selectSharedState } from 'containers/SharedState/selectors';
import { actions } from 'containers/Visibility/IncidentResponse/slice';

import { selectIncidentResponse } from '../../../selectors';

import { getHourlyAccessSummaryConfig } from './config';

export interface HourlyAccessSummaryProps {
  accountId?: string | null;
  date: string;
  isOpen: boolean;
  onClose: () => void;
  resourceArn?: string | null;
  resourceInsight?: Record<string, any>;
  resourceName: string;
  resourceType: string;
}

export const HourlyAccessSummary = ({
  accountId,
  date,
  isOpen,
  onClose,
  resourceArn,
  resourceInsight: resourceInsightProp,
  resourceName,
  resourceType,
}: HourlyAccessSummaryProps) => {
  const dispatch = useDispatch();
  const { environmentId } = useAccessBoundary();
  const { accessSummary, resourceRiskContext } = useSelector(
    selectIncidentResponse,
  );
  const { resourceInsight: resourceInsightState } = useSelector(
    selectSharedState,
  );
  const { accountMapping, renderAccountWithVPCAndRegion } = useCloudAccountId();
  const resourceInsight = resourceInsightProp ?? resourceInsightState.data;
  const accountResourceInsight = useMemo(() => {
    const accountIdentifier = resourceInsight?.account ?? accountId;
    const accountUuid =
      (accountIdentifier && accountMapping[accountIdentifier]
        ? accountIdentifier
        : undefined) ??
      Object.entries(accountMapping).find(
        ([, account]) =>
          String(account?.cloud_account_id) === String(accountIdentifier) ||
          String(account?.cloud_account_id) === String(accountId),
      )?.[0];

    if (!accountUuid) return undefined;

    return {
      ...resourceInsight,
      account: accountUuid,
    };
  }, [accountId, accountMapping, resourceInsight]);
  const accountDetails = accountResourceInsight
    ? renderAccountWithVPCAndRegion(accountResourceInsight)
    : accountId ?? resourceInsight?.account ?? '-';
  const filteredAccessSummary = useMemo(() => {
    const selectedDate = dayjs(date).format('YYYY-MM-DD');
    const isOnSelectedDate = (point: { hour?: String }) =>
      !point.hour ||
      dayjs(String(point.hour)).format('YYYY-MM-DD') === selectedDate;

    return {
      ...accessSummary,
      data: {
        ...accessSummary.data,
        api_calls: accessSummary.data?.api_calls?.filter(isOnSelectedDate),
        data_accessed: accessSummary.data?.data_accessed?.filter(
          isOnSelectedDate,
        ),
        by_action_type: accessSummary.data?.by_action_type?.filter(
          isOnSelectedDate,
        ),
      },
    };
  }, [accessSummary, date]);
  const dashboardConfig = useMemo(
    () => getHourlyAccessSummaryConfig(filteredAccessSummary),
    [filteredAccessSummary],
  );
  const labels = resourceInsight?.user_tags;
  const insights = resourceInsight?.insights;
  console.log(labels);

  const renderTags = (values: any) => {
    if (!Array.isArray(values) || !values.length) return '-';

    return (
      <HStack spacing={1.5} flexWrap="wrap">
        {values.map((value, index) => (
          <Tag
            key={`${String(value?.name ?? value)}-${index}`}
            label={String(value?.name ?? value)}
          />
        ))}
      </HStack>
    );
  };

  useEffect(() => {
    if (!isOpen || !resourceArn) return;

    const selectedDate = dayjs(date);

    dispatch(
      actions.getCloudEventsAccessSummary({
        q: {
          body: {
            resource_arn: resourceArn,
            date: selectedDate.format(),
            start: selectedDate.startOf('day').utc().format(),
            end: selectedDate.endOf('day').utc().format(),
            account_id: accountId || undefined,
            environment_id: environmentId,
            resource_type:
              (resourceType as V1CloudEventsResourceType) ??
              V1CloudEventsResourceType.SimpleStorageService,
          },
        },
      }),
    );
  }, [
    accountId,
    date,
    dispatch,
    environmentId,
    isOpen,
    resourceArn,
    resourceType,
  ]);

  useEffect(() => {
    if (!isOpen || !resourceInsight?.uuid) return;

    dispatch(
      actions.getResourceRiskContext({
        q: {
          riskContextRequest: {
            uuids: [resourceInsight.uuid],
          },
        },
      }),
    );
  }, [dispatch, isOpen, resourceInsight?.uuid]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      header={
        <StackedHeader
          upper={`Hourly Access Summary for ${resourceName}`}
          lower={dayjs(date).format('D MMMM YYYY')}
          type={resourceType}
          color={getIconColor('aws')}
          useCustomColor
          border
          borderColor={customTheme.colors.yellow['300']}
        />
      }
      body={
        <Stack h="full" w="full" spacing={5} pt={3}>
          <SimpleGrid columns={2} spacingX={10} spacingY={5} px={3}>
            <DetailsIdentifier label="Account" value={accountDetails} />
            <DetailsIdentifier label="Labels" value={renderTags(labels)} />
            <DetailsIdentifier
              label="Risk Context"
              isLoading={resourceRiskContext.isLoading}
              value={renderRiskContext(
                resourceRiskContext.data?.[resourceInsight?.uuid] ?? [],
              )}
            />
            <DetailsIdentifier label="Insights" value={renderTags(insights)} />
          </SimpleGrid>
          <Box flex={1} w="full">
            <CDashboard dashboardWidgets={dashboardConfig} />
          </Box>
        </Stack>
      }
      closeButton
      styles={{
        content: { maxW: '6xl' },
        body: { overflowY: 'auto' },
        drawer: {
          blockScrollOnMount: false,
          trapFocus: false,
        },
      }}
    />
  );
};
